import { useMediaQuery, useTheme } from '@mui/material';
import { ComponentType, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { DesktopModalDialog } from './styles/ModalViewStyles';
import { useCreateCollectionCache } from '../../../cache/learner/createCollectionReactiveVar';
import { useVerificationCodeCache } from '../../../cache/learner/verificationCodeReactiveVar';
import { CollectionActionEnum } from '../../../constants/enums/CollectionActionEnum';
import { getTestAttrs, TestIdContext } from '../../../constants/testingAttributes';
import { ModalContext } from '../../../contexts/ModalContext';
import { useChatBotOverlayRegistration } from '../../../contexts/OverlayOffsetContext';
import { PATH_WIZARD } from '../../../routes/paths';
import { styleConfigs } from '../../resources/styleConfigs';
import DrawerWrapper from '../../wrappers/DrawerWrapper';
import ModalDialogAnimated from '../ModalDialogAnimated/ModalDialogAnimated';

interface IHandleDimensionsInsideCollectionModal {
  desktopWidth?: number;
  desktopHeight?: string;
  mobileHeight?: string;
  mobileRoundedCorner?: boolean;
  mobileMarginInline?: number;
}

const ModalView: React.FC = () => {
  const { pathname } = useLocation();
  const isSmScreen: boolean = useMediaQuery(useTheme().breakpoints.down('sm'));
  const { modalClose, isOpen, contentData } = useContext(ModalContext);
  const { disableCloseOnClickBackdrop, disableCloseOnPressScape } = contentData;
  const ActiveComponent: ComponentType<{ directProps?: unknown }> = contentData?.component;
  const { createCollectionId } = useCreateCollectionCache();
  const { createVerificationCodeId } = useVerificationCodeCache();

  useChatBotOverlayRegistration({
    id: 'general-modal',
    isOpen: isSmScreen && isOpen,
  });

  const handleDimensionsInsideCollectionModal = (): IHandleDimensionsInsideCollectionModal => {
    if (createCollectionId === CollectionActionEnum.CREATE_COLLECTION) {
      return {
        desktopWidth: styleConfigs.dimensions.width620,
        desktopHeight: styleConfigs.dimensions.auto,
      };
    }

    if (createVerificationCodeId === CollectionActionEnum.VERIFICATION_CODE) {
      return {
        desktopWidth: styleConfigs.dimensions.width504,
        desktopHeight: styleConfigs.dimensions.auto,
        mobileHeight: styleConfigs.dimensions.auto,
        mobileRoundedCorner: true,
        mobileMarginInline: styleConfigs.marginPadding.custom16,
      };
    }

    return {};
  };

  const handleClose = (event: unknown, reason: 'escapeKeyDown' | 'backdropClick' | undefined): void => {
    if (reason === 'escapeKeyDown' && disableCloseOnPressScape) {
      return;
    }
    if (reason === 'backdropClick' && disableCloseOnClickBackdrop) {
      return;
    }
    modalClose();
  };

  if (isSmScreen) {
    if (contentData.mobileUseDrawer) {
      return (
        <DrawerWrapper
          {...getTestAttrs(TestIdContext.MODAL, 'modal_drawer')}
          anchor="bottom"
          open={isOpen}
          onClose={(event, reason) => handleClose(event, reason)}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              borderTopLeftRadius: styleConfigs.borders.radius16,
              borderTopRightRadius: styleConfigs.borders.radius16,
              width: styleConfigs.dimensions.percent100,
            },
          }}
        >
          <ActiveComponent directProps={contentData?.directProps} />
        </DrawerWrapper>
      );
    }

    return (
      <ModalDialogAnimated
        {...getTestAttrs(TestIdContext.MODAL, 'modal_view')}
        data-cy-modal-type="mobile"
        data-cy-is-open={isOpen}
        isOpen={isOpen}
        onClose={() => handleClose(null, undefined)}
        backdropClick={disableCloseOnClickBackdrop}
        escapeKeyClick={disableCloseOnPressScape}
        canBeSwipped={pathname !== PATH_WIZARD.steps.information}
        mobileWidth={contentData.mobileWidth}
        mobileHeight={handleDimensionsInsideCollectionModal().mobileHeight ?? contentData.mobileHeight}
        mobileRoundedCorner={
          handleDimensionsInsideCollectionModal().mobileRoundedCorner ?? contentData.mobileRoundedCorner
        }
        mobileMarginInline={
          handleDimensionsInsideCollectionModal().mobileMarginInline ?? contentData.mobileMarginInline
        }
      >
        <ActiveComponent directProps={contentData?.directProps} />
      </ModalDialogAnimated>
    );
  }

  return (
    <DesktopModalDialog
      {...getTestAttrs(TestIdContext.MODAL, 'modal_view')}
      data-cy-modal-type="desktop"
      data-cy-is-open={isOpen}
      open={isOpen}
      onClose={handleClose}
      keepMounted
      maxWidth="xl"
      desktopWidth={handleDimensionsInsideCollectionModal().desktopWidth ?? contentData.desktopWidth}
      desktopHeight={handleDimensionsInsideCollectionModal().desktopHeight ?? contentData.desktopHeight}
    >
      <ActiveComponent directProps={contentData?.directProps} />
    </DesktopModalDialog>
  );
};

export default ModalView;
